import { SUPABASE_URL } from '@/config';
import { getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';

const CACHE_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

export type SmartleadConnectionStatus = {
  isConnected: boolean;
  connectedAt: string | null;
};

export type SmartleadCampaign = {
  id: number;
  name: string;
  status: string;
  totalLeads: number;
  sent: number;
  opened: number;
  replied: number;
  bounced: number;
  clicked: number;
  unsubscribed: number;
  openRate: number;
  replyRate: number;
  bounceRate: number;
  createdAt: string | null;
};

export type SmartleadOverview = {
  totalCampaigns: number;
  activeCampaigns: number;
  totalSent: number;
  totalOpened: number;
  totalReplied: number;
  totalBounced: number;
  totalClicked: number;
  avgOpenRate: number;
  avgReplyRate: number;
  avgBounceRate: number;
  campaigns: SmartleadCampaign[];
  lastSyncedAt: string | null;
};

async function invoke(body: Record<string, any>): Promise<any> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const res = await fetch(
    `${SUPABASE_URL}/functions/v1/smartlead`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token ?? ''}`,
      },
      body: JSON.stringify({ ...body, userId: session?.user?.id, organizationId: getActiveOrgId() }),
    }
  );

  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `Edge function error ${res.status}`);
  if (data?.error) throw new Error(data.error);
  return data;
}

function mapCacheRow(row: any): SmartleadCampaign {
  return {
    id: row.campaign_id,
    name: row.name,
    status: row.status,
    totalLeads: row.total_leads,
    sent: row.sent,
    opened: row.opened,
    replied: row.replied,
    bounced: row.bounced,
    clicked: row.clicked,
    unsubscribed: row.unsubscribed,
    openRate: row.open_rate,
    replyRate: row.reply_rate,
    bounceRate: row.bounce_rate,
    createdAt: row.campaign_created_at ?? null,
  };
}

function buildOverview(campaigns: SmartleadCampaign[], lastSyncedAt: string | null): SmartleadOverview {
  let totalSent = 0;
  let totalOpened = 0;
  let totalReplied = 0;
  let totalBounced = 0;
  let totalClicked = 0;

  for (const c of campaigns) {
    totalSent += c.sent;
    totalOpened += c.opened;
    totalReplied += c.replied;
    totalBounced += c.bounced;
    totalClicked += c.clicked;
  }

  const activeCampaigns = campaigns.filter(
    (c) => c.status === 'ACTIVE' || c.status === 'START'
  ).length;

  return {
    totalCampaigns: campaigns.length,
    activeCampaigns,
    totalSent,
    totalOpened,
    totalReplied,
    totalBounced,
    totalClicked,
    avgOpenRate: totalSent > 0 ? Math.round((totalOpened / totalSent) * 100) : 0,
    avgReplyRate: totalSent > 0 ? Math.round((totalReplied / totalSent) * 100) : 0,
    avgBounceRate: totalSent > 0 ? Math.round((totalBounced / totalSent) * 100) : 0,
    campaigns,
    lastSyncedAt,
  };
}

const smartleadService = {
  async getConnectionStatus(): Promise<SmartleadConnectionStatus> {
    const orgId = getActiveOrgId();
    let query = supabase
      .from('smartlead_connections')
      .select('connected_at')
      .eq('is_active', true);

    if (orgId) query = query.eq('organization_id', orgId);

    const { data } = await query.maybeSingle();

    return {
      isConnected: !!data,
      connectedAt: data?.connected_at ?? null,
    };
  },

  async connect(apiKey: string): Promise<void> {
    await invoke({ action: 'connect', apiKey });
    // Sync immediately after connecting
    await invoke({ action: 'sync' });
  },

  async disconnect(): Promise<void> {
    await invoke({ action: 'disconnect' });
  },

  /**
   * Trigger a sync from Smartlead API → local cache.
   */
  async sync(): Promise<void> {
    await invoke({ action: 'sync' });
  },

  /**
   * Read campaigns from local cache. Auto-syncs if cache is stale (>1 hour).
   */
  async getOverview(): Promise<SmartleadOverview> {
    const { data: rows } = await supabase
      .from('smartlead_campaign_cache')
      .select('*')
      .order('campaign_id', { ascending: false });

    const campaigns = (rows ?? []).map(mapCacheRow);
    const lastSyncedAt = rows?.[0]?.synced_at ?? null;

    // Auto-sync if cache is empty or stale
    const isStale = !lastSyncedAt ||
      (Date.now() - new Date(lastSyncedAt).getTime()) > CACHE_MAX_AGE_MS;

    if (isStale) {
      try {
        await invoke({ action: 'sync' });
        // Re-read fresh data
        const { data: freshRows } = await supabase
          .from('smartlead_campaign_cache')
          .select('*')
          .order('campaign_id', { ascending: false });

        const freshCampaigns = (freshRows ?? []).map(mapCacheRow);
        const freshSyncedAt = freshRows?.[0]?.synced_at ?? null;
        return buildOverview(freshCampaigns, freshSyncedAt);
      } catch {
        // If sync fails, return stale data rather than nothing
        return buildOverview(campaigns, lastSyncedAt);
      }
    }

    return buildOverview(campaigns, lastSyncedAt);
  },
};

export default smartleadService;
